import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody } from 'reactstrap';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { calculatePercentDiff } from './BidPriceLogic';

export interface CostBreakdownItem {
  description: string;
  contractorAmount: string;
  homeownerAmount: string;
}

export interface Bid {
  id?: string;
  contractorId?: string;
  contractorName: string;
  bidAmount: string;
  bidAmountContractor: string;
  startDate: string;
  endDate: string;
  approach: string;
  costBreakdown: CostBreakdownItem[];
  status?: 'Submitted' | 'Accepted';
  bidType: string;
  fixedPriceType: string | null;
  contractorLicense?: string;
  contractorAddress?: string;
  contractorCity?: string;
  contractorState?: string;
  contractorZip?: string;
  contractorPhone?: string;
  contractorEmail?: string;
  warrantyPeriod: string;
  requireInspection: boolean;
  terminationNoticeDays: string;
  delayDays: string;
  attachments: string[];
  createdAt?: firebase.firestore.Timestamp;
  paymentSchedule: Array<{ condition: string; percentage: number }>;
}

interface BidStats {
  minContractor: number;
  minHomeowner: number;
  maxContractor: number;
  maxHomeowner: number;
  avgContractor: number;
  avgHomeowner: number;
}

interface PercentDiffs {
  min: number;
  max: number;
  avg: number;
}

interface BidStatisticsProps {
  jobId: string;
  currentBidAmount: number;
  isContractor: boolean;
}

const BidStatistics: React.FC<BidStatisticsProps> = ({ jobId, currentBidAmount, isContractor }) => {
  const [bidStats, setBidStats] = useState<BidStats>({
    minContractor: 0,
    minHomeowner: 0,
    maxContractor: 0,
    maxHomeowner: 0,
    avgContractor: 0,
    avgHomeowner: 0,
  });
  const [percentDiffs, setPercentDiffs] = useState<PercentDiffs>({
    min: 0,
    max: 0,
    avg: 0,
  });

  useEffect(() => {
    const fetchBidStats = async () => {
      const firestore = firebase.firestore();
      const bidsSnapshot = await firestore.collection('jobs').doc(jobId).collection('bids').get();
      const bids = bidsSnapshot.docs.map(doc => doc.data());
      
      if (bids.length > 0) {
        const stats = bids.reduce((acc: BidStats, bid: any) => {
          const contractorAmount = parseFloat(bid.bidAmountContractor) || 0;
          const homeownerAmount = parseFloat(bid.bidAmount) || 0;
          return {
            minContractor: Math.min(acc.minContractor, contractorAmount),
            minHomeowner: Math.min(acc.minHomeowner, homeownerAmount),
            maxContractor: Math.max(acc.maxContractor, contractorAmount),
            maxHomeowner: Math.max(acc.maxHomeowner, homeownerAmount),
            avgContractor: acc.avgContractor + contractorAmount,
            avgHomeowner: acc.avgHomeowner + homeownerAmount,
          };
        }, {
          minContractor: Infinity,
          minHomeowner: Infinity,
          maxContractor: -Infinity,
          maxHomeowner: -Infinity,
          avgContractor: 0,
          avgHomeowner: 0,
        });

        stats.avgContractor /= bids.length;
        stats.avgHomeowner /= bids.length;

        setBidStats(stats);
      }
    };

    fetchBidStats();
  }, [jobId]);

  useEffect(() => {
    const baseAmount = isContractor ? bidStats.avgContractor : bidStats.avgHomeowner;
    const minAmount = isContractor ? bidStats.minContractor : bidStats.minHomeowner;
    const maxAmount = isContractor ? bidStats.maxContractor : bidStats.maxHomeowner;

    setPercentDiffs({
      min: calculatePercentDiff(currentBidAmount, minAmount),
      max: calculatePercentDiff(currentBidAmount, maxAmount),
      avg: calculatePercentDiff(currentBidAmount, baseAmount),
    });
  }, [currentBidAmount, isContractor, bidStats]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  return (
    <>
      <h2>Bid Statistics</h2>
      <Row>
        <Col lg={4} md={12}>
          <Card>
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="avatar-sm flex-shrink-0">
                  <span className="avatar-title bg-light text-primary rounded-circle fs-3">
                    <i className="align-middle ri-money-dollar-circle-fill"></i>
                  </span>
                </div>
                <div className="flex-grow-1 ms-3">
                  <p className="text-uppercase fw-semibold fs-12 text-muted mb-1">Minimum Bid</p>
                  <h4 className="mb-0">
                    <span>{formatCurrency(bidStats.minContractor)} / {formatCurrency(bidStats.minHomeowner)}</span>
                  </h4>
                  <p className="text-muted mb-0 fs-11">
                    <span>(Contractor Total) / (Homeowner Total)</span>
                  </p>
                </div>
                <div className="flex-shrink-0 align-self-end">
                  <span className={`badge ${percentDiffs.min > 0 ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'}`}>
                    <i className={`align-middle me-1 ri-arrow-${percentDiffs.min >= 0 ? 'up' : 'down'}-s-fill`}></i>
                    {isFinite(percentDiffs.min) ? Math.abs(percentDiffs.min).toFixed(2) : '0.00'}%
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg={4} md={12}>
          <Card>
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="avatar-sm flex-shrink-0">
                  <span className="avatar-title bg-light text-primary rounded-circle fs-3">
                    <i className="align-middle ri-money-dollar-circle-fill"></i>
                  </span>
                </div>
                <div className="flex-grow-1 ms-3">
                  <p className="text-uppercase fw-semibold fs-12 text-muted mb-1">Maximum Bid</p>
                  <h4 className="mb-0">
                    <span>{formatCurrency(bidStats.maxContractor)} / {formatCurrency(bidStats.maxHomeowner)}</span>
                  </h4>
                  <p className="text-muted mb-0 fs-11">
                    <span>(Contractor Total) / (Homeowner Total)</span>
                  </p>
                </div>
                <div className="flex-shrink-0 align-self-end">
                  <span className={`badge ${percentDiffs.max > 0 ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'}`}>
                    <i className={`align-middle me-1 ri-arrow-${percentDiffs.max >= 0 ? 'up' : 'down'}-s-fill`}></i>
                    {isFinite(percentDiffs.max) ? Math.abs(percentDiffs.max).toFixed(2) : '0.00'}%
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg={4} md={12}>
          <Card>
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="avatar-sm flex-shrink-0">
                  <span className="avatar-title bg-light text-primary rounded-circle fs-3">
                    <i className="align-middle ri-money-dollar-circle-fill"></i>
                  </span>
                </div>
                <div className="flex-grow-1 ms-3">
                  <p className="text-uppercase fw-semibold fs-12 text-muted mb-1">Average Bid</p>
                  <h4 className="mb-0">
                    <span>{formatCurrency(bidStats.avgContractor)} / {formatCurrency(bidStats.avgHomeowner)}</span>
                  </h4>
                  <p className="text-muted mb-0 fs-11">
                    <span>(Contractor Total) / (Homeowner Total)</span>
                  </p>
                </div>
                <div className="flex-shrink-0 align-self-end">
                  <span className={`badge ${percentDiffs.avg > 0 ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'}`}>
                    <i className={`align-middle me-1 ri-arrow-${percentDiffs.avg >= 0 ? 'up' : 'down'}-s-fill`}></i>
                    {isFinite(percentDiffs.avg) ? Math.abs(percentDiffs.avg).toFixed(2) : '0.00'}%
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default BidStatistics;